export interface HandleFields {
  tiktok: string;
  instagram: string;
}

/**
 * What the handles card should show once the server's values change underneath
 * it: a field the creator hasn't touched follows the server, a field they are
 * typing into is left alone.
 *
 * The portal's handles card stays mounted across a background refetch, so a
 * staff correction can land while a creator has the card on screen. An
 * untouched field has to follow that correction — otherwise it keeps a stale
 * value that reads as an edit and gets written back over the correction — and
 * a field being typed into must not be yanked out from under them.
 *
 * "Untouched" is decided by comparing against the values the server last gave
 * us, not by a "has ever edited" flag. A flag latches on the first keystroke
 * and never clears, so a creator who typed something and changed their mind
 * would stop receiving corrections for as long as the page stayed open.
 *
 * Both fields are resolved in one pure call, taking the current values rather
 * than returning `setState` updaters, because the two cannot be separated
 * safely: an updater runs during a later render, after the effect body has
 * already advanced the "last synced" values, so it would compare against the
 * new values and conclude every field was touched.
 */
export function syncHandleFields({
  current,
  synced,
  next,
}: {
  /** What the fields hold right now. */
  current: HandleFields;
  /** What the server last said they were. */
  synced: HandleFields;
  /** What the server says they are now. */
  next: HandleFields;
}): HandleFields {
  return {
    tiktok: current.tiktok === synced.tiktok ? next.tiktok : current.tiktok,
    instagram:
      current.instagram === synced.instagram
        ? next.instagram
        : current.instagram,
  };
}

/**
 * Whether an edited field differs from what the server holds. Shared by both
 * editors so they agree on trimming and on blank-means-no-handle rather than
 * each hand-rolling the comparison.
 */
export function handleFieldChanged(
  current: string,
  stored: string | null,
): boolean {
  return current.trim() !== (stored ?? "");
}
